import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api, TOOL_TYPES, AUTO_REPAIR_TYPES, COUNTRIES } from '@/lib/context';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { TranslatableText } from '@/components/TranslatableText';
import BudgetSection from '@/components/BudgetSection';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Wrench, Car, Home, MapPin, Clock, Loader2, Plus, ExternalLink, 
  Star, Search, Filter, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

// Shared Nav for marketplace pages
const MarketplaceNav = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.jobs')}
            </Link>
            <Link to="/tools" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.tools')}
            </Link>
            <Link to="/auto-repair" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.autoRepair')}
            </Link>
            <Link to="/services" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.services')}
            </Link>
            <Link to="/accommodation" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.accommodation')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to={user.role === 'company' ? '/company/dashboard' : user.role === 'admin' ? '/admin' : '/worker/dashboard'}>
                  <Button variant="outline" size="sm">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
                  {t('auth.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('auth.register')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Tool Rentals Page
export const ToolRentalsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({ item_type: 'all', region: '', country: 'all' });
  const [newTool, setNewTool] = useState({
    item_type: '',
    description: '',
    region: '',
    country: '',
    price_per_day: '',
    availability_notes: ''
  });

  useEffect(() => {
    fetchTools();
  }, [filters]);

  const fetchTools = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      const res = await api.getToolRentals(params);
      // Defensive: ensure we always set an array
      const data = res?.data;
      setTools(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch tools:', err);
      setError(t('marketplace.tools.unableToLoad'));
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTool = async () => {
    if (!user) {
      toast.error(t('auth.login'));
      navigate('/login');
      return;
    }

    setCreating(true);
    try {
      const data = {
        ...newTool,
        price_per_day: newTool.price_per_day ? parseFloat(newTool.price_per_day) : null
      };
      await api.createToolListing(data);
      toast.success(t('marketplace.tools.success'));
      setCreateOpen(false);
      setNewTool({ item_type: '', description: '', region: '', country: '', price_per_day: '', availability_notes: '' });
      fetchTools();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tool-rentals-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('marketplace.tools.title')}</h1>
            <p className="text-muted-foreground">{t('marketplace.tools.subtitle')}</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="list-tool-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('marketplace.tools.listTool')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('marketplace.tools.createTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('marketplace.tools.toolTypeLabel')} *</Label>
                  <Select value={newTool.item_type} onValueChange={(v) => setNewTool({...newTool, item_type: v})}>
                    <SelectTrigger data-testid="tool-type-select">
                      <SelectValue placeholder={t('marketplace.tools.toolTypePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {TOOL_TYPES.map((toolType) => (
                        <SelectItem key={toolType} value={toolType}>{t(`toolTypes.${toolType}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('marketplace.tools.descriptionLabel')} *</Label>
                  <Textarea
                    placeholder={t('marketplace.tools.descriptionPlaceholder')}
                    value={newTool.description}
                    onChange={(e) => setNewTool({...newTool, description: e.target.value})}
                    data-testid="tool-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('marketplace.tools.countryLabel')} *</Label>
                    <Select value={newTool.country} onValueChange={(v) => setNewTool({...newTool, country: v})}>
                      <SelectTrigger data-testid="tool-country-select">
                        <SelectValue placeholder={t('auth.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('marketplace.tools.regionLabel')} *</Label>
                    <Input
                      placeholder={t('marketplace.tools.regionPlaceholder')}
                      value={newTool.region}
                      onChange={(e) => setNewTool({...newTool, region: e.target.value})}
                      data-testid="tool-region-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('marketplace.tools.priceLabel')}</Label>
                  <Input
                    type="number"
                    placeholder={t('marketplace.tools.pricePlaceholder')}
                    value={newTool.price_per_day}
                    onChange={(e) => setNewTool({...newTool, price_per_day: e.target.value})}
                    data-testid="tool-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('marketplace.tools.availabilityLabel')} *</Label>
                  <Input
                    placeholder={t('marketplace.tools.availabilityPlaceholder')}
                    value={newTool.availability_notes}
                    onChange={(e) => setNewTool({...newTool, availability_notes: e.target.value})}
                    data-testid="tool-availability-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleCreateTool} disabled={creating} data-testid="submit-tool-btn">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('marketplace.tools.submitButton')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Select value={filters.item_type} onValueChange={(v) => setFilters({...filters, item_type: v})}>
                <SelectTrigger data-testid="filter-tool-type">
                  <SelectValue placeholder={t('marketplace.tools.toolTypeLabel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allTypes')}</SelectItem>
                  {TOOL_TYPES.map((toolType) => (
                    <SelectItem key={toolType} value={toolType}>{t(`toolTypes.${toolType}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                <SelectTrigger data-testid="filter-tool-country">
                  <SelectValue placeholder={t('marketplace.tools.countryLabel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allCountries')}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder={t('marketplace.tools.regionLabel')}
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                data-testid="filter-tool-region"
              />
              <Button variant="outline" onClick={() => setFilters({ item_type: '', region: '', country: '' })}>
                {t('marketplace.filters.clear')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tools Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tools-loading">
            {[1,2,3,4,5,6].map((i) => (
              <Card key={i}><CardContent className="pt-6"><Skeleton className="h-32" /></CardContent></Card>
            ))}
          </div>
        ) : error ? (
          <Card className="text-center py-12" data-testid="tools-error">
            <CardContent>
              <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('common.somethingWentWrong')}</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchTools}>
                {t('common.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        ) : !tools || tools.length === 0 ? (
          <Card className="text-center py-12" data-testid="tools-empty">
            <CardContent>
              <Wrench className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('marketplace.tools.noTools')}</p>
              <p className="text-muted-foreground">{t('marketplace.tools.beFirst')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tools-grid">
            {tools.map((tool) => {
              // Defensive null checks for each tool item
              if (!tool || !tool.id) return null;
              
              return (
                <Card key={tool.id} className="card-hover" data-testid={`tool-card-${tool.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{tool.item_type ? t(`toolTypes.${tool.item_type}`) : t('marketplace.tools.unknownTool')}</CardTitle>
                        <CardDescription>{tool.owner_nickname || t('common.anonymous')}</CardDescription>
                      </div>
                      {tool.price_per_day != null && (
                        <Badge className="bg-brand text-white">€{tool.price_per_day}{t('toolRentals.perDay')}</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{tool.description || t('common.noDescription')}</p>
                    <div className="flex flex-wrap gap-2">
                      {(tool.region || tool.country) && (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {[tool.region, tool.country].filter(Boolean).join(', ')}
                        </Badge>
                      )}
                    </div>
                    {tool.availability_notes && (
                      <p className="text-xs text-muted-foreground mt-2">{tool.availability_notes}</p>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button size="sm" className="w-full" data-testid={`contact-tool-${tool.id}`}>
                      {t('marketplace.tools.contactOwner')}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Auto Repair Page
export const AutoRepairPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({ issue_type: 'all', region: '', country: 'all' });
  const [newRequest, setNewRequest] = useState({
    issue_type: '',
    description: '',
    region: '',
    country: '',
    vehicle_info: ''
  });

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const fetchRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      const res = await api.getAutoRepairRequests(params);
      // Defensive: ensure requests is always an array
      const data = res?.data;
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch auto repair requests:', err);
      setError(t('marketplace.autoRepair.unableToLoad'));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!user) {
      toast.error(t('auth.login'));
      navigate('/login');
      return;
    }

    setCreating(true);
    try {
      await api.createAutoRepairRequest(newRequest);
      toast.success(t('marketplace.autoRepair.success'));
      setCreateOpen(false);
      setNewRequest({ issue_type: '', description: '', region: '', country: '', vehicle_info: '' });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="auto-repair-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('marketplace.autoRepair.title')}</h1>
            <p className="text-muted-foreground">{t('marketplace.autoRepair.subtitle')}</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="post-repair-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('marketplace.autoRepair.postRequest')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('marketplace.autoRepair.createTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('marketplace.autoRepair.issueTypeLabel')} *</Label>
                  <Select value={newRequest.issue_type} onValueChange={(v) => setNewRequest({...newRequest, issue_type: v})}>
                    <SelectTrigger data-testid="repair-type-select">
                      <SelectValue placeholder={t('marketplace.autoRepair.issueTypePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {AUTO_REPAIR_TYPES.map((repairType) => (
                        <SelectItem key={repairType} value={repairType}>{t(`repairTypes.${repairType}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('jobs.description')} *</Label>
                  <Textarea
                    placeholder={t('marketplace.autoRepair.descriptionPlaceholder')}
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({...newRequest, description: e.target.value})}
                    data-testid="repair-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('marketplace.autoRepair.vehicleLabel')}</Label>
                  <Input
                    placeholder={t('marketplace.autoRepair.vehiclePlaceholder')}
                    value={newRequest.vehicle_info}
                    onChange={(e) => setNewRequest({...newRequest, vehicle_info: e.target.value})}
                    data-testid="repair-vehicle-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('jobs.country')} *</Label>
                    <Select value={newRequest.country} onValueChange={(v) => setNewRequest({...newRequest, country: v})}>
                      <SelectTrigger data-testid="repair-country-select">
                        <SelectValue placeholder={t('auth.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('jobs.region')} *</Label>
                    <Input
                      placeholder={t('marketplace.tools.regionPlaceholder')}
                      value={newRequest.region}
                      onChange={(e) => setNewRequest({...newRequest, region: e.target.value})}
                      data-testid="repair-region-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleCreateRequest} disabled={creating} data-testid="submit-repair-btn">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('marketplace.autoRepair.submitButton')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Select value={filters.issue_type} onValueChange={(v) => setFilters({...filters, issue_type: v})}>
                <SelectTrigger data-testid="filter-repair-type">
                  <SelectValue placeholder={t('marketplace.autoRepair.issueTypeLabel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allTypes')}</SelectItem>
                  {AUTO_REPAIR_TYPES.map((repairType) => (
                    <SelectItem key={repairType} value={repairType}>{t(`repairTypes.${repairType}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                <SelectTrigger data-testid="filter-repair-country">
                  <SelectValue placeholder={t('jobs.country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allCountries')}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder={t('jobs.region')}
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                data-testid="filter-repair-region"
              />
              <Button variant="outline" onClick={() => setFilters({ issue_type: '', region: '', country: '' })}>
                {t('marketplace.filters.clear')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Requests Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 gap-6" data-testid="auto-repair-loading">
            {[1,2,3,4].map((i) => (
              <Card key={i}><CardContent className="pt-6"><Skeleton className="h-32" /></CardContent></Card>
            ))}
          </div>
        ) : error ? (
          <Card className="text-center py-12" data-testid="auto-repair-error">
            <CardContent>
              <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('common.somethingWentWrong')}</p>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchRequests}>
                {t('common.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        ) : !requests || requests.length === 0 ? (
          <Card className="text-center py-12" data-testid="auto-repair-empty">
            <CardContent>
              <Car className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-xl font-semibold mb-2">{t('marketplace.autoRepair.noRequests')}</p>
              <p className="text-muted-foreground">{t('marketplace.autoRepair.postToGetQuotes')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="auto-repair-grid">
            {requests.map((req) => {
              // Defensive null checks
              if (!req || !req.id) return null;
              
              return (
                <Card key={req.id} className="card-hover" data-testid={`repair-card-${req.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{req.issue_type ? t(`repairTypes.${req.issue_type}`) : t('common.unknownItem')}</CardTitle>
                        {req.vehicle_info && <CardDescription>{req.vehicle_info}</CardDescription>}
                      </div>
                      {req.urgency && (
                        <Badge variant={req.urgency === 'emergency' ? 'destructive' : req.urgency === 'urgent' ? 'default' : 'secondary'}>
                          {t(`services.${req.urgency}`)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">{req.description || t('common.noDescription')}</p>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      {[req.region, req.country].filter(Boolean).join(', ') || t('common.locationNotSpecified')}
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between">
                    <span className="text-xs text-muted-foreground">{req.responses_count || 0} {t('autoRepair.responses')}</span>
                    <Button size="sm" data-testid={`respond-repair-${req.id}`}>{t('marketplace.autoRepair.respond')}</Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Accommodation Page
export const AccommodationPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="accommodation-page">
        <h1 className="text-3xl font-bold mb-4">{t('marketplace.accommodation.title')}</h1>
        <p className="text-muted-foreground mb-8">{t('marketplace.accommodation.subtitle')}</p>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-6 w-6 text-brand" />
                {t('marketplace.accommodation.airbnbTitle')}
              </CardTitle>
              <CardDescription>{t('marketplace.accommodation.airbnbDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t('marketplace.accommodation.airbnbText')}
              </p>
            </CardContent>
            <CardFooter>
              <a href="https://www.airbnb.com" target="_blank" rel="noopener noreferrer" className="w-full">
                <Button className="w-full" data-testid="airbnb-link">
                  {t('marketplace.accommodation.browseAirbnb')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </CardFooter>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-6 w-6 text-brand" />
                {t('marketplace.accommodation.bookingTitle')}
              </CardTitle>
              <CardDescription>{t('marketplace.accommodation.bookingDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {t('marketplace.accommodation.bookingText')}
              </p>
            </CardContent>
            <CardFooter>
              <a href="https://www.booking.com" target="_blank" rel="noopener noreferrer" className="w-full">
                <Button className="w-full" data-testid="booking-link">
                  {t('marketplace.accommodation.browseBooking')}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </CardFooter>
          </Card>
        </div>

        <Card className="mt-8 bg-muted/50">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <Clock className="h-8 w-8 text-brand flex-shrink-0" />
              <div>
                <h3 className="font-semibold mb-2">{t('marketplace.accommodation.comingSoon')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('marketplace.accommodation.comingSoonText')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Service Requests Page
export const ServiceRequestsPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({ category: 'all', region: '', country: 'all' });
  const [newRequest, setNewRequest] = useState({
    category: '',
    description: '',
    region: '',
    country: '',
    urgency: 'normal',
    budget: null
  });

  const SERVICE_CATEGORIES = ['construction', 'renovation', 'plumbing', 'electrical', 'painting', 'cleaning', 'moving', 'landscaping', 'hvac', 'other'];

  useEffect(() => {
    fetchRequests();
  }, [filters]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') params[key] = value;
      });
      const res = await api.getServiceRequests(params);
      setRequests(res.data);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!user) {
      toast.error(t('auth.login'));
      navigate('/login');
      return;
    }

    setCreating(true);
    try {
      await api.createServiceRequest(newRequest);
      toast.success(t('marketplace.services.success'));
      setCreateOpen(false);
      setNewRequest({ category: '', description: '', region: '', country: '', urgency: 'normal', budget: null });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceNav />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="service-requests-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('marketplace.services.title')}</h1>
            <p className="text-muted-foreground">{t('marketplace.services.subtitle')}</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="post-service-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('marketplace.services.postRequest')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('marketplace.services.createTitle')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('marketplace.services.categoryLabel')} *</Label>
                  <Select value={newRequest.category} onValueChange={(v) => setNewRequest({...newRequest, category: v})}>
                    <SelectTrigger data-testid="service-category-select">
                      <SelectValue placeholder={t('marketplace.services.categoryPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('jobs.description')} *</Label>
                  <Textarea
                    placeholder={t('marketplace.services.descriptionPlaceholder')}
                    value={newRequest.description}
                    onChange={(e) => setNewRequest({...newRequest, description: e.target.value})}
                    data-testid="service-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('marketplace.services.urgencyLabel')}</Label>
                  <Select value={newRequest.urgency} onValueChange={(v) => setNewRequest({...newRequest, urgency: v})}>
                    <SelectTrigger data-testid="service-urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">{t('services.normal')}</SelectItem>
                      <SelectItem value="urgent">{t('services.urgent')}</SelectItem>
                      <SelectItem value="emergency">{t('services.emergency')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('jobs.country')} *</Label>
                    <Select value={newRequest.country} onValueChange={(v) => setNewRequest({...newRequest, country: v})}>
                      <SelectTrigger data-testid="service-country-select">
                        <SelectValue placeholder={t('auth.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('jobs.region')} *</Label>
                    <Input
                      placeholder={t('marketplace.tools.regionPlaceholder')}
                      value={newRequest.region}
                      onChange={(e) => setNewRequest({...newRequest, region: e.target.value})}
                      data-testid="service-region-input"
                    />
                  </div>
                </div>
                {/* Universal Budget block */}
                <div className="pt-2 border-t">
                  <BudgetSection
                    category={newRequest.category}
                    country={newRequest.country}
                    value={newRequest.budget}
                    onChange={(b) => setNewRequest({...newRequest, budget: b})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleCreateRequest} disabled={creating} data-testid="submit-service-btn">
                  {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('marketplace.services.submitButton')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Content similar to auto repair - filters and grid */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Select value={filters.category} onValueChange={(v) => setFilters({...filters, category: v})}>
                <SelectTrigger data-testid="filter-service-category">
                  <SelectValue placeholder={t('marketplace.services.categoryLabel')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allCategories')}</SelectItem>
                  {SERVICE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v})}>
                <SelectTrigger data-testid="filter-service-country">
                  <SelectValue placeholder={t('jobs.country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('marketplace.filters.allCountries')}</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{t(c.nameKey)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder={t('jobs.region')}
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                data-testid="filter-service-region"
              />
              <Button variant="outline" onClick={() => setFilters({ category: '', region: '', country: '' })}>
                {t('marketplace.filters.clear')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1,2,3,4].map((i) => <Skeleton key={i} className="h-40" />)}
          </div>
        ) : requests.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-semibold mb-2">{t('marketplace.services.noRequests')}</p>
              <p className="text-muted-foreground">{t('marketplace.services.postToFind')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {requests.map((req) => (
              <ServiceRequestCard key={req.id} request={req} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Service Request Card with dynamic translation - NO Estonian fallbacks
const ServiceRequestCard = ({ request }) => {
  const { t, language } = useLanguage();
  const { text: translatedDesc, isTranslated } = useTranslatedContent(request.description);
  const { text: translatedRegion } = useTranslatedContent(request.region);
  
  // Get translated category - fallback to humanized id if key missing
  const categoryKey = `categories.${request.category}`;
  const translatedCategory = t(categoryKey);
  const isHit = translatedCategory && translatedCategory !== categoryKey
    && !translatedCategory.startsWith?.('[MISSING:');
  const humanizedCat = String(request.category || '')
    .split('_')
    .map(w => w ? w[0].toUpperCase() + w.slice(1) : '')
    .join(' ');
  const categoryDisplay = isHit ? translatedCategory : humanizedCat;
  
  // Get translated urgency - must use proper key
  const urgencyKey = `services.${request.urgency}`;
  const translatedUrgency = t(urgencyKey);
  const urgencyDisplay = translatedUrgency !== urgencyKey ? translatedUrgency : t('services.normal');
  
  // Get translated country
  const countryKey = `location.countries.${request.country}`;
  const translatedCountry = t(countryKey);
  const countryDisplay = translatedCountry !== countryKey ? translatedCountry : request.country;
  
  return (
    <Card className="card-hover" data-testid={`service-card-${request.id}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">
            <TranslatableText text={categoryDisplay} />
          </CardTitle>
          <Badge variant={request.urgency === 'emergency' ? 'destructive' : request.urgency === 'urgent' ? 'default' : 'secondary'}>
            {urgencyDisplay}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {translatedDesc}
          {isTranslated && (
            <Languages className="inline-block ml-2 h-3.5 w-3.5 text-blue-500" title={t('common.translated')} />
          )}
        </p>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          {translatedRegion}, {countryDisplay}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <span className="text-xs text-muted-foreground">{request.responses_count} {t('autoRepair.responses')}</span>
        <Button size="sm" data-testid={`respond-service-${request.id}`}>{t('marketplace.services.respond')}</Button>
      </CardFooter>
    </Card>
  );
};
